import {useMemo, useState} from "react";

import {Card, CardContent} from "@/components/ui/card";
import {CitationDetailModal} from "@/src/modules/citations/components/citation-detail-modal";
import {useCitations} from "@/src/modules/citations/hooks/use-citations";
import {citationsService} from "@/src/modules/citations/services";
import {Loader} from "@/src/modules/core/components";
import useResidentHouseholdId from "@/src/modules/core/hooks/use-resident-household-id";
import useResidentMembership from "@/src/modules/core/hooks/use-resident-membership";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {cn} from "@/lib/utils";

import {
  RESIDENT_HOME_BLOCK_TITLE_CLASS,
  RESIDENT_HOME_CARD_CONTENT_CLASS,
  RESIDENT_HOME_CARD_SHELL_CLASS,
  RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
  RESIDENT_HOME_PILLAR_FILLER_CLASS,
  RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS,
  RESIDENT_HOME_PILLAR_LIST_SCROLL_CLASS,
  RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS,
  RESIDENT_HOME_RECORD_PILLAR_PREVIEW_LIMIT,
  RESIDENT_HOME_TEXT_INSET_CLASS,
} from "../lib/resident-home-block-layout";
import {useHideResidentHomeTenantData} from "../hooks/use-hide-resident-home-tenant-data";
import {RESIDENT_HOME_CITATIONS_ICON} from "../lib/resident-home-teaser";
import {ResidentHomeCardPreviewFooter} from "./resident-home-card-preview-footer";
import {ResidentHomeCitationsPreviewRow} from "./resident-home-citations-preview-row";
import {ResidentHomeCitationsSheet} from "./resident-home-citations-sheet";
import {ResidentHomeIconTextBlock} from "./resident-home-icon-text-block";
import {ResidentHomeNoTenantData} from "./resident-home-no-tenant-data";

const PREVIEW_LIMIT = RESIDENT_HOME_RECORD_PILLAR_PREVIEW_LIMIT;

type ResidentHomeCitationsCardProps = {
  className?: string;
};

export function ResidentHomeCitationsCard({className}: ResidentHomeCitationsCardProps) {
  const userProperty = useStoredUserProperty();
  const householdId = useResidentHouseholdId();
  const membership = useResidentMembership();
  const hideTenantData = useHideResidentHomeTenantData();

  const [expandOpen, setExpandOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const canShow = citationsService.getCanShow();

  const filter = useMemo(
    () =>
      userProperty?.id && householdId
        ? {context_property_id: userProperty.id, household_id: householdId}
        : {},
    [householdId, userProperty?.id],
  );

  const {citations, totalRecords, isPending} = useCitations({
    rows: PREVIEW_LIMIT,
    page: 1,
    filters: filter,
    sort: "-citation_date",
    enabled: !hideTenantData && Boolean(userProperty?.id && householdId && canShow),
  });

  const showExpand = totalRecords > PREVIEW_LIMIT;

  return (
    <>
      <Card className={cn(RESIDENT_HOME_CARD_SHELL_CLASS, "flex h-full min-h-0 flex-col", className)}>
        <CardContent
          className={cn(
            RESIDENT_HOME_CARD_CONTENT_CLASS,
            RESIDENT_HOME_PILLAR_CARD_CONTENT_CLASS,
            "min-h-0 flex-1",
          )}
        >
          <div className={RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS}>
            <ResidentHomeIconTextBlock
              icon={RESIDENT_HOME_CITATIONS_ICON.icon}
              iconWrapperClassName={RESIDENT_HOME_CITATIONS_ICON.iconWrapperClassName}
              iconClassName={RESIDENT_HOME_CITATIONS_ICON.iconClassName}
              className="min-w-0 flex-1 items-start"
              title={<p className={RESIDENT_HOME_BLOCK_TITLE_CLASS}>Citations</p>}
            />
            <span className={RESIDENT_HOME_PILLAR_HEADER_ACTION_SLOT_CLASS} aria-hidden>
              ·
            </span>
          </div>

          <div className={cn(RESIDENT_HOME_PILLAR_LIST_SLOT_CLASS, RESIDENT_HOME_TEXT_INSET_CLASS)}>
            {hideTenantData ? (
              <ResidentHomeNoTenantData />
            ) : !canShow ? (
              <p className="text-muted-foreground text-sm">
                You do not have permission to view citations.
              </p>
            ) : !householdId ? (
              <p className="text-muted-foreground text-sm">
                Link a household to view citations.
              </p>
            ) : isPending ? (
              <Loader className="min-h-24" />
            ) : citations.length === 0 ? (
              <p className="text-muted-foreground text-sm">No citations yet.</p>
            ) : (
              <div className={RESIDENT_HOME_PILLAR_LIST_SCROLL_CLASS}>
                {citations.map((row) => (
                  <ResidentHomeCitationsPreviewRow
                    key={row.citation_id}
                    citation={row}
                    membership={membership}
                    onSelect={(id) => canShow && setDetailId(id)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={RESIDENT_HOME_PILLAR_FILLER_CLASS} aria-hidden />

          {!hideTenantData && canShow && householdId && !isPending && citations.length > 0 ? (
            <ResidentHomeCardPreviewFooter
              showingCount={citations.length}
              totalRecords={totalRecords}
              onShowMore={showExpand ? () => setExpandOpen(true) : undefined}
            />
          ) : (
            <div aria-hidden className={RESIDENT_HOME_PILLAR_FOOTER_PLACEHOLDER_CLASS} />
          )}
        </CardContent>
      </Card>

      <ResidentHomeCitationsSheet
        open={expandOpen}
        onOpenChange={setExpandOpen}
        filter={filter}
        householdId={householdId}
        canShowDetail={canShow}
        onSelectDetail={(id) => setDetailId(id)}
      />

      <CitationDetailModal
        open={detailId != null}
        citationId={detailId}
        onOpenChange={(open) => !open && setDetailId(null)}
      />
    </>
  );
}
